import { Button, TextInput } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";

import style from "./passports-view.scss";
import Battery from "components/icons/Battery";
import CancelMark from "components/icons/CancelMark";
import EmptyStateIcon from "components/icons/EmptyState";
import LayeredLevels from "components/icons/LayeredLevels";
import Line from "components/icons/Line";
import Print from "components/icons/Print";
import RoundTick from "components/icons/RoundTick";
import SelectedCheckmark from "components/icons/SelectedCheckmark";
import ShieldIcon from "components/icons/ShieldIcon";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { REPORTS_ROUTE } from "components/router/Routes";
import { FetchPassportsResponse, PassportReport, passportService } from "services/report/passports/PassportService";
import { StoreState } from "store";
import { createDateLabel } from "utils/format";

const mapState = (state: StoreState) => ({
    theme: state.themeReducer.theme,
});

const connector = connect(mapState);

interface DetailRow {
    label: string;
    value: string;
}

const DIAGNOSTIC_KEYS: Array<keyof NonNullable<PassportReport["diagnostic_results"]>> = [
    "battery_charging",
    "oem_parts",
    "touch",
    "screen",
    "front_camera",
    "rear_camera",
    "speaker",
    "wifi",
    "bluetooth",
    "vibration",
    "face_id",
    "fingerprint",
];

const isPass = (value: string | undefined): boolean | null => {
    if (value === undefined || value === null) {
        return null;
    }
    const diagnostic_value = value.toLowerCase();
    if (diagnostic_value === "successful" || diagnostic_value === "passed") {
        return true;
    }
    if (diagnostic_value === "failed") {
        return false;
    }
    return null;
};

const DetailPanel = ({
    title,
    rows,
    footer,
    icon,
}: {
    title: string;
    rows: DetailRow[];
    footer?: JSX.Element;
    icon?: JSX.Element;
}): JSX.Element => (
    <div className={style.detailPanel}>
        <div className={style.detailPanelHeader}>
            {icon && <span className={style.detailPanelIcon}>{icon}</span>}
            <span className={style.detailPanelTitle}>{title}</span>
        </div>
        <div className={style.detailPanelRows}>
            {rows.map((row) => (
                <div key={row.label} className={style.detailRow}>
                    <span className={style.detailLabel}>{row.label}</span>
                    <span className={style.detailValue}>{row.value}</span>
                </div>
            ))}
        </div>
        {footer}
    </div>
);

const DiagnosticsPanel = ({
    title,
    diagnostics,
    footer,
    theme,
    icon,
}: {
    title: string;
    diagnostics: PassportReport["diagnostic_results"];
    footer?: JSX.Element;
    theme: ReturnType<typeof mapState>["theme"];
    icon?: JSX.Element;
}): JSX.Element => {
    const { t } = useTranslation();
    const leftCol = DIAGNOSTIC_KEYS.filter((_, i) => i % 2 === 0);
    const rightCol = DIAGNOSTIC_KEYS.filter((_, i) => i % 2 !== 0);

    const renderItem = (key: keyof NonNullable<PassportReport["diagnostic_results"]>) => {
        const value = diagnostics?.[key] as string | undefined;
        const passed = isPass(value);
        return (
            <div key={key} className={style.diagnosticItem}>
                {passed === true ? (
                    <SelectedCheckmark color={theme.accordianSuccessTextColor} />
                ) : passed === false ? (
                    <CancelMark color={theme.errorTextColor} />
                ) : (
                    <Line color={theme.neutralBadgeTextColor} />
                )}
                <span className={style.diagnosticLabel}>{t(`DevicePassport.panel.diagnostic.${key}`)}</span>
            </div>
        );
    };

    return (
        <div className={style.detailPanel}>
            <div className={style.detailPanelHeader}>
                {icon && <span className={style.detailPanelIcon}>{icon}</span>}
                <span className={style.detailPanelTitle}>{title}</span>
            </div>
            <div className={style.diagnosticsGrid}>
                <div className={style.diagnosticsCol}>{leftCol.map(renderItem)}</div>
                <div className={style.diagnosticsCol}>{rightCol.map(renderItem)}</div>
            </div>
            {footer}
        </div>
    );
};

const NA = "N/A";

const PassportResultCard = ({
    report,
    theme,
}: {
    report: PassportReport;
    theme: ReturnType<typeof mapState>["theme"];
}): JSX.Element => {
    const { t } = useTranslation();
    const { device_information, device_insights, erasure_details, diagnostic_results } = report;

    const identifiers = [
        device_information?.imei && `${t("DevicePassport.panel.device_information.imei")}: ${device_information.imei}`,
        device_information?.imei_two &&
            `${t("DevicePassport.panel.device_information.imei_two")}: ${device_information.imei_two}`,
        device_information?.serial &&
            `${t("DevicePassport.panel.device_information.serial")}: ${device_information.serial}`,
        report.last_processed && `${t("DevicePassport.lastProcessed")}: ${createDateLabel(report.last_processed)}`,
    ].filter(Boolean) as string[];

    const deviceInsightRows: DetailRow[] = [
        { label: t("DevicePassport.panel.device_insights.blacklisted"), value: device_insights?.blacklisted ?? NA },
        { label: t("DevicePassport.panel.device_insights.graylisted"), value: device_insights?.graylisted ?? NA },
        { label: t("DevicePassport.panel.device_insights.simlock"), value: device_insights?.simlock ?? NA },
        { label: t("DevicePassport.panel.device_insights.carrier_name"), value: device_insights?.carrier_name ?? NA },
        { label: t("DevicePassport.panel.device_insights.mdm_status"), value: device_insights?.mdm_status ?? NA },
        {
            label: t("DevicePassport.panel.device_insights.lost_stolen_blocked"),
            value: device_insights?.lost_stolen_blocked ?? NA,
        },
    ];

    const hasBatteryInfo =
        diagnostic_results?.battery_capacity !== undefined || diagnostic_results?.battery_charge_cycles !== undefined;

    const diagnosticsFooter = hasBatteryInfo ? (
        <div className={style.batteryBadge}>
            <Battery color={theme.accordianSuccessTextColor} />
            <span className={style.batteryText}>
                {diagnostic_results?.battery_capacity !== undefined &&
                    `${diagnostic_results.battery_capacity}% capacity`}
                {diagnostic_results?.battery_capacity !== undefined &&
                    diagnostic_results?.battery_charge_cycles !== undefined &&
                    " - "}
                {diagnostic_results?.battery_charge_cycles !== undefined &&
                    `${diagnostic_results.battery_charge_cycles} charge cycles`}
            </span>
        </div>
    ) : undefined;

    const erasureRows: DetailRow[] = [
        { label: t("DevicePassport.panel.erasure_details.standard"), value: erasure_details?.standard ?? NA },
        { label: t("DevicePassport.panel.erasure_details.state"), value: erasure_details?.state ?? NA },
        { label: t("DevicePassport.panel.erasure_details.performed"), value: erasure_details?.performed ?? NA },
        { label: t("DevicePassport.panel.erasure_details.duration"), value: erasure_details?.duration ?? NA },
        {
            label: t("DevicePassport.panel.erasure_details.passes"),
            value: erasure_details?.passes !== undefined ? String(erasure_details.passes) : NA,
        },
        { label: t("DevicePassport.panel.erasure_details.target.model"), value: erasure_details?.target?.model ?? NA },
        { label: t("DevicePassport.panel.erasure_details.target.type"), value: erasure_details?.target?.type ?? NA },
        {
            label: t("DevicePassport.panel.erasure_details.target.capacity"),
            value: erasure_details?.target?.capacity ?? NA,
        },
    ];

    return (
        <>
            <div className={style.resultCard}>
                <p className={style.resultModel}>{device_information?.model ?? NA}</p>
                <div className={style.resultIdentifiers}>
                    {identifiers.map((id) => (
                        <span key={id}>{id}</span>
                    ))}
                </div>
                <div className={style.detailPanelsRow}>
                    <DetailPanel
                        title={t("DevicePassport.panel.deviceInsights")}
                        rows={deviceInsightRows}
                        icon={<LayeredLevels color={theme.linkTextColor} />}
                    />
                    <DiagnosticsPanel
                        title={t("DevicePassport.panel.diagnostics")}
                        diagnostics={diagnostic_results}
                        footer={diagnosticsFooter}
                        theme={theme}
                        icon={<RoundTick color={theme.accordianSuccessTextColor} />}
                    />
                    <DetailPanel
                        title={t("DevicePassport.panel.erasureDetails")}
                        rows={erasureRows}
                        icon={<ShieldIcon color={theme.linkTextColor} />}
                    />
                </div>
            </div>
            <div className={style.printBar}>
                <span className={style.printLabel}>{t("DevicePassport.printLabel")}</span>
                <Button variant="Primary" icon={<Print color="currentColor" />} onClick={() => window.print()}>
                    {t("DevicePassport.print")}
                </Button>
            </div>
        </>
    );
};

const PassportsViewComponent = (props: ConnectedProps<typeof connector>): JSX.Element => {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [requestFailureMessage, setRequestFailureMessage] = React.useState("");
    const [result, setResult] = React.useState<FetchPassportsResponse | null>(null);
    const abortControllerRef = React.useRef<AbortController | null>(null);

    const handleSearch = () => {
        if (!searchValue.trim()) {
            return;
        }
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        setRequestFailureMessage("");
        setResult(null);

        passportService
            .fetchPassports(searchValue.trim(), abortController)
            .then((response) => {
                setResult(response);
            })
            .catch(() => {
                setRequestFailureMessage(t("DevicePassport.requestFailed"));
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const view = (
        <div className={style.container}>
            <div className={style.card}>
                <h2 className={style.title}>{t("DevicePassport.title")}</h2>
                <p className={style.subtitle}>{t("DevicePassport.subtitle")}</p>
                <div className={style.searchRow}>
                    <TextInput
                        name="passport-search"
                        label=""
                        hideLabelSection
                        placeholder={t("DevicePassport.inputPlaceholder")}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className={style.searchInput}
                    />
                    <Button variant="Primary" onClick={handleSearch} disabled={loading || !searchValue.trim()}>
                        {t("DevicePassport.searchButton")}
                    </Button>
                </div>
                <p className={style.footnote}>{t("DevicePassport.footnote")}</p>
            </div>
            {loading && (
                <div className={style.resultsArea}>
                    <LoadingIndicator />
                </div>
            )}
            {requestFailureMessage && (
                <div className={style.resultsArea}>
                    <p className={style.errorMessage}>{requestFailureMessage}</p>
                </div>
            )}
            {result && !loading && result.reports.length === 0 && (
                <div className={style.resultsArea}>
                    <EmptyStateIcon />
                    <p className={style.noResults}>{t("DevicePassport.noResults")}</p>
                </div>
            )}
            {result &&
                !loading &&
                result.reports.map((report, index) => (
                    <PassportResultCard key={index} report={report} theme={props.theme} />
                ))}
        </div>
    );

    return <ApplicationLayout viewTitle={t("Routes.devicePassport")} view={view} mainRoute={REPORTS_ROUTE} />;
};

const PassportsView = connector(PassportsViewComponent);

export default PassportsView;
